import { useEffect, useMemo, useState } from 'react';
import { listIntegrations, listRequests } from '../../../../services';
import { validateApiResponse } from '../../../../shared/utils/apiResponse';

/** Normalize an integration name for fuzzy duplicate matching (CT-4205). */
const normalizeName = (s) => (s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '').trim();

// Ask for every connected integration, not just the service's default first
// page (50), so a duplicate name beyond page 1 can't slip past the Case-1
// warning (PR #288 review).
const CONNECTED_PAGE_SIZE = 1000;

/**
 * Duplicate-request detection for the Request Integration modal (CT-4205).
 *
 * On open, loads the two sources a submission could duplicate — connected
 * integrations (Case 1) and the current user's own open requests (Case 2) — and
 * fuzzy-matches the typed `name` (normalized: lowercased, punctuation-stripped)
 * against each. Kept separate from `useRequestIntegrationForm` so the form hook
 * stays focused on form state / validation / submit.
 *
 * `ready` is false until this open's fetch settles; the form gates submit on it
 * so a request can't be filed before the pending-duplicate check has run (and
 * it resets on each reopen, so stale data from a prior open can't leak) — PR
 * #290 review.
 *
 * @param {{ isOpen?: boolean, requesterId?: string, name?: string }} params
 * @returns {{ duplicateConnected: object|null, duplicatePending: object|null, ready: boolean }}
 */
export function useDuplicateDetection({ isOpen, requesterId, name } = {}) {
  const [connected, setConnected] = useState([]);
  const [myOpenRequests, setMyOpenRequests] = useState([]);
  const [ready, setReady] = useState(false);

  // Reset readiness on each open/close transition during render (React's
  // recommended alternative to a setState-in-effect, which the lint rule flags
  // as cascading). The effect below flips it back true once the load settles.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    setReady(false);
  }

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    // Load the two sources independently (allSettled): a failure in one must not
    // wipe the other's signal, and parsing is guarded so a bad payload can't
    // either (PR #290 review).
    Promise.allSettled([
      listIntegrations({ filters: { connected: true }, page_size: CONNECTED_PAGE_SIZE }),
      listRequests({ requester_id: requesterId }),
    ]).then(([connRes, reqRes]) => {
      if (cancelled) return;
      if (connRes.status === 'fulfilled') {
        try {
          setConnected(validateApiResponse(connRes.value).body ?? []);
        } catch {
          /* keep the prior connected list */
        }
      }
      if (reqRes.status === 'fulfilled') {
        try {
          const open = (validateApiResponse(reqRes.value).body ?? []).filter(
            (r) => r.status === 'pending' || r.status === 'in_progress',
          );
          setMyOpenRequests(open);
        } catch {
          /* keep the prior open-requests list */
        }
      }
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, requesterId]);

  const normalizedName = normalizeName(name);
  const duplicateConnected = useMemo(
    () => (normalizedName ? connected.find((i) => normalizeName(i.name) === normalizedName) ?? null : null),
    [connected, normalizedName],
  );
  const duplicatePending = useMemo(
    () =>
      normalizedName
        ? myOpenRequests.find((r) => normalizeName(r.name) === normalizedName) ?? null
        : null,
    [myOpenRequests, normalizedName],
  );

  return { duplicateConnected, duplicatePending, ready };
}
